import { Injectable } from '@angular/core';
import { cloneDeep } from 'lodash';
import { Observable, of, Subject, throwError } from 'rxjs';
import { tap } from 'rxjs/operators';
import { dcTag } from './model/dcTag.model';

interface CacheContent {
  expiry: number;
  value: any;
}

/**
 * Cache Service is an observables based in-memory cache implementation
 * Keeps track of in-flight observables and sets a default expiry for cached values
 *
 * @export
 * @class CacheService
 */
@Injectable()
export class CacheService {
  private cache: Map<string, CacheContent> = new Map<string, CacheContent>();
  private inFlightObservables: Map<string, Subject<any>> = new Map<string, Subject<any>>();
  readonly DEFAULT_MAX_AGE = 15000000; //4hrs aprox.

  /**
   * Gets the value from cache if the key is provided.
   * If no value exists in cache, then check if the same call exists
   * in flight, if so return the subject. If not create a new
   * Subject inFlightObservable and return the source observable.
   */
  get<T = any>(
    key: string,
    fallback?: Observable<T>,
    maxAge?: number,
    clone?: boolean,
    forceReload?: boolean
  ): Observable<T> | Subject<T> {
    if (forceReload) {
      this.clearCache(key);
    }

    if (this.hasValidCachedValue(key)) {
      console.log(`%cGetting from cache ${key}`, 'color: green');
      return of(clone ? cloneDeep(this.cache.get(key).value) : this.cache.get(key).value);
    }

    if (!maxAge) {
      maxAge = this.DEFAULT_MAX_AGE;
    }

    if (this.inFlightObservables.has(key)) {
      return this.inFlightObservables.get(key);
    } else if (fallback && fallback instanceof Observable) {
      this.inFlightObservables.set(key, new Subject());
      console.log(`%c Calling api for ${key}`, 'color: purple');
      return fallback.pipe(
        tap({
          next: value => {
            this.set(key, clone ? cloneDeep(value) : value, maxAge);
          },
          error: err => {
            // `fallback` are crashed
            // crashed flight is terrible😰, it's better to clean it up...
            this.inFlightObservables.delete(key);
            // and when we have done our job, it's good idea to let the others know this event.
            // maybe they have their stuffs need to be done too.
            return throwError(() => err);
          },
          complete: () => this.inFlightObservables.delete(key)
        })
      );
    } else {
      return throwError(() => 'Requested key is not available in Cache');
    }
  }

  /**
   * Sets the value with key in the cache
   * Notifies all observers of the new value
   */
  set(key: string, value: any, maxAge = this.DEFAULT_MAX_AGE): void {
    this.cache.set(key, { value, expiry: Date.now() + maxAge });
    this.notifyInFlightObservers(key, value);
  }

  /**
   * Append the value with key in the cache
   * Notifies all observers of the new value
   */
  appendData(key: string, newDataToAppend: any): void {
    if (!newDataToAppend || !this.hasValidCachedValue(key)) {
      return;
    }

    const existingData = this.cache.get(key);
    if (!existingData || !Array.isArray(existingData.value)) return;

    console.log(`%Appending from cache ${key}`, 'color: green');

    existingData.value.push(newDataToAppend);
    this.cache.set(key, { value: existingData.value, expiry: existingData.expiry });
    this.notifyInFlightObservers(key, existingData.value);
  }

  /**
   * Checks if the a key exists in cache
   */
  has(key: string): boolean {
    return this.cache.has(key);
  }

  /**
   * Publishes the value to all observers of the given
   * in progress observables if observers exist.
   */
  private notifyInFlightObservers(key: string, value: any): void {
    if (this.inFlightObservables.has(key)) {
      const inFlight = this.inFlightObservables.get(key);
      if (inFlight.observed) {
        console.log(
          `%cNotifying ${inFlight.observers.length} flight subscribers for ${key}`,
          'color: blue'
        );
        inFlight.next(value);
      }
      inFlight.complete();
      this.inFlightObservables.delete(key);
    }
  }

  /**
   * Checks if the key exists and   has not expired.
   */
  private hasValidCachedValue(key: string): boolean {
    if (this.cache.has(key)) {
      if (this.cache.get(key).expiry < Date.now()) {
        this.cache.delete(key);
        return false;
      }
      return true;
    } else {
      return false;
    }
  }

  public generateCacheKeyForTags(tags: dcTag[]) {
    const tagKey = tags.map(tag => this.generateCacheKeyForTag(tag)).join('');
    return tagKey;
  }

  public generateCacheKeyForTag(tag: dcTag): string {
    return `${tag.TagID}${tag.TagName}${tag.ObjectID || tag.SourceObjectID}${tag.ElementTypeID}`;
  }

  public clearInflightRequest(cacheKey: string) {
    if (this.inFlightObservables) {
      this.inFlightObservables.delete(cacheKey);
    }
  }

  public clearCache(cacheKey: string) {
    this.clearInflightRequest(cacheKey);
    this.cache.delete(cacheKey);
  }
}
